import {
  formatFeePercent,
  formatPounds,
} from '@/pages/directory/utils/directory-fee-display';
import {
  getFeeAtVatBasis,
  type FeeVatBasis,
  type PublishedFee,
} from '@/utils/fee-maths';

export const standardFeeCount = 8;

type FeeLine = {
  amountPence?: number | null;
  percent?: string | null;
  sourceUrls?: ReadonlyArray<string | null | undefined> | null;
} | null;

type OtherFeeLine = {
  label?: string | null;
  amountPence?: number | null;
  percent?: string | null;
  sourceUrls?: ReadonlyArray<string | null | undefined> | null;
};

type StandardFeeKey =
  | 'management'
  | 'rentCollection'
  | 'tenantFind'
  | 'letOnly'
  | 'renewal'
  | 'inventory'
  | 'managementWithTenantInPlace'
  | 'rentCollectionWithTenantInPlace';

export type DirectoryAgentFeesInput = {
  feesIncludeVat?: boolean | null;
  management?: FeeLine;
  rentCollection?: FeeLine;
  tenantFind?: FeeLine;
  letOnly?: FeeLine;
  renewal?: FeeLine;
  inventory?: FeeLine;
  managementWithTenantInPlace?: FeeLine;
  rentCollectionWithTenantInPlace?: FeeLine;
  otherFees?: ReadonlyArray<OtherFeeLine | null | undefined> | null;
};

function getStandardFee(
  fees: DirectoryAgentFeesInput | null | undefined,
  key: StandardFeeKey,
): FeeLine {
  if (fees == null) {
    return null;
  }

  switch (key) {
    case 'management':
      return fees.management ?? null;
    case 'rentCollection':
      return fees.rentCollection ?? null;
    case 'tenantFind':
      return fees.tenantFind ?? null;
    case 'letOnly':
      return fees.letOnly ?? null;
    case 'renewal':
      return fees.renewal ?? null;
    case 'inventory':
      return fees.inventory ?? null;
    case 'managementWithTenantInPlace':
      return fees.managementWithTenantInPlace ?? null;
    case 'rentCollectionWithTenantInPlace':
      return fees.rentCollectionWithTenantInPlace ?? null;
    default: {
      const _exhaustive: never = key;
      return _exhaustive;
    }
  }
}

type FeeTableGroupId = 'service' | 'start' | 'while' | 'ending' | 'other';

type DirectoryAgentFeeTableRow = {
  label: string;
  amountLabel: string;
  isPublished: boolean;
  sourceUrls: readonly string[];
};

type DirectoryAgentFeeTableGroup = {
  id: FeeTableGroupId;
  title: string;
  note: string;
  rows: DirectoryAgentFeeTableRow[];
};

export type DirectoryAgentFeeTable = {
  groups: DirectoryAgentFeeTableGroup[];
  publishedCount: number;
  unpublishedCount: number;
  hasAnyFee: boolean;
  vatAmountColumnLabel: string;
};

const groupMeta: Record<FeeTableGroupId, { title: string; note: string }> = {
  service: {
    title: 'Service fees',
    note: 'The headline percentage for each level of service',
  },
  start: {
    title: 'Start of each tenancy',
    note: 'Usually paid once, when a new tenant moves in',
  },
  while: {
    title: 'While the tenant stays',
    note: 'Charges that recur or come up during a tenancy',
  },
  ending: {
    title: 'Ending or leaving',
    note: 'What it costs to stop using the agent',
  },
  other: {
    title: 'Other',
    note: 'Additional fees from published documents',
  },
};

const namedFeeDefinitions: {
  groupId: FeeTableGroupId;
  label: string;
  key: StandardFeeKey;
}[] = [
  { groupId: 'service', label: 'Full management', key: 'management' },
  { groupId: 'service', label: 'Rent collection', key: 'rentCollection' },
  {
    groupId: 'service',
    label: 'Management only (tenant already in place)',
    key: 'managementWithTenantInPlace',
  },
  {
    groupId: 'service',
    label: 'Rent collection (tenant already in place)',
    key: 'rentCollectionWithTenantInPlace',
  },
  { groupId: 'service', label: 'Let only', key: 'letOnly' },
  { groupId: 'service', label: 'Tenant find', key: 'tenantFind' },
  {
    groupId: 'start',
    label: 'Inventory and check-in',
    key: 'inventory',
  },
  {
    groupId: 'while',
    label: 'Renewal or continuation',
    key: 'renewal',
  },
];

const standardFeeKeys = namedFeeDefinitions.map((definition) => definition.key);

function isStandardFeePublished(fee: FeeLine): boolean {
  if (fee == null) {
    return false;
  }

  return fee.percent != null || fee.amountPence != null;
}

function normalizeSourceUrls(
  urls: ReadonlyArray<string | null | undefined> | null | undefined,
): readonly string[] {
  if (urls == null) {
    return [];
  }

  return urls.flatMap((url) => (url == null ? [] : [url]));
}

function formatAmountLabel(options: {
  fee: PublishedFee;
  feesIncludeVat: boolean | null | undefined;
  vatBasis: FeeVatBasis;
}): { amountLabel: string; isPublished: boolean } {
  const atBasis = getFeeAtVatBasis(options);

  if (atBasis.status === 'notPublished') {
    return { amountLabel: 'Not published', isPublished: false };
  }

  const percent =
    atBasis.status === 'published' || atBasis.status === 'vatNotStated'
      ? atBasis.percent
      : null;
  const amountPence =
    atBasis.status === 'published' || atBasis.status === 'vatNotStated'
      ? atBasis.amountPence
      : null;

  if (percent != null) {
    return {
      amountLabel: formatFeePercent(percent),
      isPublished: true,
    };
  }

  if (amountPence != null) {
    return {
      amountLabel: formatPounds(amountPence),
      isPublished: true,
    };
  }

  return { amountLabel: 'Not published', isPublished: false };
}

const endingWordPatterns = ['early'] as const;

function labelIncludesPattern(normalized: string, pattern: string): boolean {
  if (pattern === 'early') {
    return /\bearly\b/u.test(normalized);
  }

  return normalized.includes(pattern);
}

function classifyOtherFeeGroup(label: string): FeeTableGroupId {
  const normalized = label.trim().toLowerCase();

  const endingPatterns = [
    'check-out',
    'checkout',
    'terminat',
    'end of tenancy',
    'notice',
    'sell',
    'sale',
    'withdraw',
    'deposit return',
    'dispute',
  ];
  if (
    endingPatterns.some((pattern) =>
      labelIncludesPattern(normalized, pattern),
    ) ||
    endingWordPatterns.some((pattern) =>
      labelIncludesPattern(normalized, pattern),
    )
  ) {
    return 'ending';
  }

  const startPatterns = [
    'set-up',
    'setup',
    'tenancy agreement',
    'admin',
    'referenc',
    'right to rent',
    'deposit regist',
    'protect',
    'inventory',
    'check-in',
  ];
  if (startPatterns.some((pattern) => normalized.includes(pattern))) {
    return 'start';
  }

  const whilePatterns = [
    'renew',
    'extension',
    'continu',
    'rent review',
    'section 13',
    'inspect',
    'visit',
    'repair',
    'works',
    'maintenance',
    'guarantee',
    'gas',
    'epc',
    'electrical',
    'eicr',
    'safety',
    'certificate',
    'legal',
    'statement',
    'tax',
    'hmrc',
  ];
  if (whilePatterns.some((pattern) => normalized.includes(pattern))) {
    return 'while';
  }

  return 'other';
}

function buildRow(options: {
  label: string;
  fee: PublishedFee;
  sourceUrls: ReadonlyArray<string | null | undefined> | null | undefined;
  feesIncludeVat: boolean | null | undefined;
  vatBasis: FeeVatBasis;
}): DirectoryAgentFeeTableRow {
  const { amountLabel, isPublished } = formatAmountLabel({
    fee: options.fee,
    feesIncludeVat: options.feesIncludeVat,
    vatBasis: options.vatBasis,
  });

  return {
    label: options.label,
    amountLabel,
    isPublished,
    sourceUrls: normalizeSourceUrls(options.sourceUrls),
  };
}

function feeTableAmountColumnLabel(
  feesIncludeVat: boolean | null | undefined,
  vatBasis: FeeVatBasis,
): string {
  if (feesIncludeVat == null) {
    return 'As published';
  }

  return vatBasis === 'incVat' ? 'Inc VAT' : 'Ex VAT';
}

export function buildDirectoryAgentFeeTable(options: {
  fees: DirectoryAgentFeesInput | null | undefined;
  vatBasis: FeeVatBasis;
}): DirectoryAgentFeeTable {
  const { fees, vatBasis } = options;
  const feesIncludeVat = fees?.feesIncludeVat;

  const rowsByGroup = new Map<FeeTableGroupId, DirectoryAgentFeeTableRow[]>();
  const ensureGroup = (groupId: FeeTableGroupId) => {
    if (!rowsByGroup.has(groupId)) {
      rowsByGroup.set(groupId, []);
    }

    return rowsByGroup.get(groupId) ?? [];
  };

  const publishedCount = standardFeeKeys.reduce((count, key) => {
    const fee = getStandardFee(fees, key);
    return isStandardFeePublished(fee) ? count + 1 : count;
  }, 0);

  const hasOtherFees = (fees?.otherFees?.length ?? 0) > 0;
  const hasAnyFee = publishedCount > 0 || hasOtherFees;

  if (!hasAnyFee) {
    return {
      groups: [],
      publishedCount: 0,
      unpublishedCount: standardFeeCount,
      hasAnyFee: false,
      vatAmountColumnLabel: feeTableAmountColumnLabel(feesIncludeVat, vatBasis),
    };
  }

  for (const definition of namedFeeDefinitions) {
    const fee = getStandardFee(fees, definition.key);
    ensureGroup(definition.groupId).push(
      buildRow({
        label: definition.label,
        fee,
        sourceUrls: fee?.sourceUrls,
        feesIncludeVat,
        vatBasis,
      }),
    );
  }

  for (const otherFee of fees?.otherFees ?? []) {
    if (otherFee == null) {
      continue;
    }

    const label = otherFee.label?.trim() ?? 'Other fee';
    const groupId = classifyOtherFeeGroup(label);
    ensureGroup(groupId).push(
      buildRow({
        label,
        fee: otherFee,
        sourceUrls: otherFee.sourceUrls,
        feesIncludeVat,
        vatBasis,
      }),
    );
  }

  const groupOrder: FeeTableGroupId[] = [
    'service',
    'start',
    'while',
    'ending',
    'other',
  ];

  const groups = groupOrder.flatMap((groupId) => {
    const rows = rowsByGroup.get(groupId);
    if (rows == null || rows.length === 0) {
      return [];
    }

    const meta = groupMeta[groupId];
    return [
      {
        id: groupId,
        title: meta.title,
        note: meta.note,
        rows,
      },
    ];
  });

  return {
    groups,
    publishedCount,
    unpublishedCount: standardFeeCount - publishedCount,
    hasAnyFee: true,
    vatAmountColumnLabel: feeTableAmountColumnLabel(feesIncludeVat, vatBasis),
  };
}
